import { UniButton, UniIconButton } from '@/design-system/components/buttons';
import { ButtonVariant } from '@/design-system/components/buttons/useButton.hook';
import { ModalHeader } from '@/design-system/components/modal';
import { TextBody, TextCaption, TextTitle } from '@/design-system/components/typography';
import { Pressable, ScrollView, SxProp, View } from 'dripsy';
import React, { useState, useContext } from 'react';
import { useTranslation } from 'react-i18next';
import { UniInput, UniInputAmount } from '@/design-system/components/inputs';
import { LayoutContext } from 'contexts/LayoutContext';
import { useNavigation } from '@react-navigation/native';
import { NativeStackNavigationProp } from '@react-navigation/native-stack';
import { AccountsTransfersStackParamList, AccountsTransfersStackRoutes } from './type';
import { RootStackRoutes } from 'types';
import { AccountManagementStackRoutes } from '@/features/account-management/account.navigator';
import { Icon } from '@/design-system/components/icons/Icon';
import { formatAmountByDecimals } from '@/utils/numberFormatting';
import { CurrenciesContext } from '@/currencies/currency.context';
import { CurrenciesIcon } from '@/features/earn/components/CurrenciesIcon';

export interface AccountsTransfersProps {
  onClose: () => void;
  onLayout: (event: any) => void;
  account: string;
  tab?: number;
  onData: (data: any) => void;
  func?: (data: any, onSuccess?: (data: any) => void, onError?: (error: any) => void) => void;
  onReturnData?: (data: any) => void;
  refresh?: () => void;
  record?: any;
}

export const AccountsTransfersWebZeroScreen: React.FC<AccountsTransfersProps> = (props) => {
  const { onClose, onLayout, account, tab, onData, func, onReturnData, refresh, record } = props;
  const { t } = useTranslation('account-transfer');
  const layout = useContext(LayoutContext);
  const { body, btnView, textMb, btn, accountSx } = sxStyles;

  const navigation = useNavigation<NativeStackNavigationProp<AccountsTransfersStackParamList>>();
  const navigationAny = useNavigation<any>();

  const [xmetadata, setXmetadata] = useState<Array<{ key: string; value: string }>>([]);
  const [accountTransfer, setAccountTransfer] = useState<any>({
    asset: undefined,
    sourceAccount: undefined,
    destinationAccount: undefined,
    amount: undefined,
    description: undefined,
    xmetadata: {}
  });
  const [errorMsg, setErrorMsg] = useState<any>({
    sourceAccount: undefined,
    destinationAccount: undefined,
    amount: undefined,
    xmetadata: undefined
  });

  const [showView, setShowView] = useState(false);
  const onChangeView = () => {
    setShowView(!showView);
  };

  const validationForm = async (data?: { [key: string]: any | undefined }) => {
    let validate = true;
    const keys = data ? Object.keys(data) : [];
    const keysLength = keys.length;
    const errorMsgFlag = { ...errorMsg };
    if ((!keysLength && !accountTransfer.sourceAccount) || (keys.includes('sourceAccount') && !data?.sourceAccount)) {
      errorMsgFlag.sourceAccount = t('card-account-management::glossary.field-required');
      validate = false;
    } else if (!keysLength || keys.includes('sourceAccount')) {
      errorMsgFlag.sourceAccount = undefined;
    }

    if ((!keysLength && !accountTransfer.destinationAccount) || (keys.includes('destinationAccount') && !data?.destinationAccount)) {
      errorMsgFlag.destinationAccount = t('card-account-management::glossary.field-required');
      validate = false;
    } else if (!keysLength || keys.includes('destinationAccount')) {
      errorMsgFlag.destinationAccount = undefined;
    }

    if ((!keysLength && !accountTransfer.amount) || (keys.includes('amount') && !data?.amount)) {
      errorMsgFlag.amount = t('card-account-management::glossary.field-required');
      validate = false;
    } else if (
      (accountTransfer.sourceAccount && !keysLength && accountTransfer.sourceAccount?.balance < accountTransfer.amount) ||
      (keys.includes('amount') && data?.amount && accountTransfer.sourceAccount?.balance < data.amount)
    ) {
      errorMsgFlag.amount = t('card-account-management::glossary.insufficient-balance');
      validate = false;
    } else if (!keysLength || keys.includes('amount')) {
      errorMsgFlag.amount = undefined;
    }

    if ((!keysLength && xmetadata.length) || (keys.includes('xmetadata') && xmetadata.length)) {
      let xmetadataErrorMsg: Array<{ key: string; value: string }> = [];
      xmetadata.forEach((x, i) => {
        const xd = xmetadata.find((xm, xmi) => xm.key == x.key && i != xmi);
        if (!x.key) {
          xmetadataErrorMsg[i] = { ...xmetadataErrorMsg[i], key: t('card-account-management::glossary.field-required') };
          validate = false;
        } else if (xd) {
          xmetadataErrorMsg[i] = { ...xmetadataErrorMsg[i], key: t('card-account-management::glossary.key-already-exists') };
          validate = false;
        }
      });

      errorMsgFlag.xmetadata = xmetadataErrorMsg;
    }

    setErrorMsg(errorMsgFlag);

    return validate;
  };

  const changeAccountsTransfers = async () => {
    const validate = await validationForm();
    if (validate) {
      xmetadata.forEach((x) => (accountTransfer.xmetadata[x.key] = x.value));
      onData({ data: accountTransfer });
      navigation.navigate(AccountsTransfersStackRoutes.Confirm, {
        onLayout,
        account,
        tab,
        data: accountTransfer,
        func,
        onReturnData,
        refresh,
        onChangeView
      });
    }
  };

  const [decimals, setDecimals] = useState(undefined);
  const walletChange = (field: string, value: any) => {
    let accountTransferFlag = accountTransfer;
    accountTransferFlag[field] = value;
    accountTransferFlag['asset'] = value?.asset;

    setDecimals(value?.decimals || undefined);
    if (
      field == 'sourceAccount' &&
      accountTransfer?.destinationAccount?.asset &&
      accountTransfer?.destinationAccount?.asset != value?.asset
    )
      accountTransferFlag['destinationAccount'] = undefined;
    else if (
      field == 'destinationAccount' &&
      accountTransfer?.sourceAccount?.asset &&
      accountTransfer?.sourceAccount?.asset != value?.asset
    )
      accountTransferFlag['sourceAccount'] = undefined;

    setAccountTransfer(accountTransferFlag);
  };

  const { getDecimalByAsset } = useContext(CurrenciesContext);
  const [fromAsset, setFromAsset] = useState(undefined);
  const [toAsset, setToAsset] = useState(undefined);

  return (
    <View
      sx={{
        height: 'auto',
        width: layout.dimensions.width,
        maxWidth: Math.min(500, layout.dimensions.width),
        maxHeight: layout.dimensions.height,
        backgroundColor: '$backgroundPrimary'
      }}
      onLayout={(event) => {
        onLayout(event);
      }}>
      <ModalHeader title={t('account-transfer::instruction-form.title--account-transfer')} closeModal={onClose} />
      <ScrollView sx={{ flex: 1, backgroundColor: 'transparent' }} nestedScrollEnabled>
        <View sx={body}>
          <TextTitle sx={textMb} semiBold>
            {t('account-transfer::instruction-form.text--fill-out-form-below')}
          </TextTitle>
          <View sx={{ gap: '$5' }}>
            <View sx={{ gap: 4 }}>
              <TextCaption semiBold>{t('account-transfer::glossary.from-account')}</TextCaption>
              <Pressable
                onPress={() => {
                  navigationAny.navigate(RootStackRoutes.AccountManagement, {
                    screen: AccountManagementStackRoutes.AccountsTransfers,
                    params: {
                      screen: AccountsTransfersStackRoutes.AccountSelect,
                      onLayout,
                      asset: fromAsset,
                      otherSelectedId: accountTransfer?.destinationAccount?.id,
                      subaccountFlag: true,
                      onSelect: (walletId: string, wallet?: any) => {
                        if (wallet?.asset != fromAsset) setFromAsset(undefined);
                        if (wallet?.asset != toAsset && !fromAsset) setToAsset(wallet?.asset);
                        setShowView(!showView);
                        walletChange('sourceAccount', wallet);
                        validationForm({ sourceAccount: wallet });
                      },
                      onChangeView
                    }
                  });
                }}>
                <View sx={{ ...accountSx, ...(accountTransfer?.sourceAccount ? { flexDirection: 'column', py: '$2' } : {}) }}>
                  <View
                    sx={{
                      justifyContent: 'space-between',
                      display: 'flex',
                      width: '100%',
                      flexDirection: 'row',
                      alignItems: 'center'
                    }}>
                    {accountTransfer?.sourceAccount ? (
                      <View sx={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: '$2' }}>
                        <CurrenciesIcon asset={accountTransfer.sourceAccount.asset} hideText />
                        <View sx={{ gap: '$1' }}>
                          <TextBody semiBold sx={{ color: '$textPrimary' }}>
                            {accountTransfer.sourceAccount.asset ? `${accountTransfer.sourceAccount.asset} ` : ''}
                            {accountTransfer?.sourceAccount?.accountLabel
                              ? t(`earn-invest::glossary.${accountTransfer?.sourceAccount?.accountLabel}`)
                              : accountTransfer?.sourceAccount?.accountName || ' - '}
                          </TextBody>
                          <TextBody sx={{ color: '$textPrimary' }}>{accountTransfer?.sourceAccount?.accountNumber}</TextBody>
                        </View>
                      </View>
                    ) : (
                      <TextCaption sx={{ color: '$borderUpload' }}>
                        {t('account-transfer::select-account.title--select-account')}
                      </TextCaption>
                    )}
                    <Icon name="arrowRight" />
                  </View>
                </View>
              </Pressable>
              {accountTransfer?.sourceAccount && (
                <View sx={{ flexDirection: 'row', justifyContent: 'space-between', mt: '$1' }}>
                  <TextCaption>{t('account-transfer::glossary.balance')}</TextCaption>
                  <TextCaption>
                    {formatAmountByDecimals({
                      value: accountTransfer?.sourceAccount?.balance,
                      decimals: getDecimalByAsset(accountTransfer?.sourceAccount?.asset),
                      suffix: accountTransfer?.sourceAccount?.asset
                    })}
                  </TextCaption>
                </View>
              )}
              {errorMsg?.sourceAccount && (
                <TextCaption danger sx={{ mt: '$1' }}>
                  {errorMsg?.sourceAccount}
                </TextCaption>
              )}
            </View>
            <View sx={{ gap: 4 }}>
              <TextCaption semiBold>{t('account-transfer::glossary.to-account')}</TextCaption>
              <Pressable
                onPress={() => {
                  navigationAny.navigate(RootStackRoutes.AccountManagement, {
                    screen: AccountManagementStackRoutes.AccountsTransfers,
                    params: {
                      screen: AccountsTransfersStackRoutes.AccountSelect,
                      onLayout,
                      asset: toAsset,
                      otherSelectedId: accountTransfer?.sourceAccount?.id,
                      balanceAllFlag: true,
                      onSelect: (walletId: string, wallet?: any) => {
                        if (wallet?.asset != fromAsset && !toAsset) setFromAsset(wallet?.asset);
                        if (wallet?.asset != toAsset) setToAsset(undefined);
                        setShowView(!showView);
                        walletChange('destinationAccount', wallet);
                        validationForm({ destinationAccount: wallet });
                      },
                      onChangeView
                    }
                  });
                }}>
                <View sx={{ ...accountSx, ...(accountTransfer?.destinationAccount ? { flexDirection: 'column', py: '$2' } : {}) }}>
                  <View
                    sx={{
                      justifyContent: 'space-between',
                      display: 'flex',
                      width: '100%',
                      flexDirection: 'row',
                      alignItems: 'center'
                    }}>
                    {accountTransfer?.destinationAccount ? (
                      <View sx={{ flexDirection: 'row', justifyContent: 'center', alignItems: 'center', gap: '$2' }}>
                        <CurrenciesIcon asset={accountTransfer.destinationAccount.asset} hideText />
                        <View sx={{ gap: '$1' }}>
                          <TextBody semiBold sx={{ color: '$textPrimary' }}>
                            {accountTransfer.destinationAccount.asset ? `${accountTransfer.destinationAccount.asset} ` : ''}
                            {accountTransfer?.destinationAccount?.accountLabel
                              ? t(`earn-invest::glossary.${accountTransfer?.destinationAccount?.accountLabel}`)
                              : accountTransfer?.destinationAccount?.accountName || ' - '}
                          </TextBody>
                          <TextBody sx={{ color: '$textPrimary' }}>{accountTransfer?.destinationAccount?.accountNumber}</TextBody>
                        </View>
                      </View>
                    ) : (
                      <TextCaption sx={{ color: '$borderUpload' }}>
                        {t('account-transfer::select-account.title--select-account')}
                      </TextCaption>
                    )}
                    <Icon name="arrowRight" />
                  </View>
                </View>
              </Pressable>
              {accountTransfer?.destinationAccount && (
                <View sx={{ flexDirection: 'row', justifyContent: 'space-between', mt: '$1' }}>
                  <TextCaption>{t('account-transfer::glossary.balance')}</TextCaption>
                  <TextCaption>
                    {formatAmountByDecimals({
                      value: accountTransfer?.destinationAccount?.balance,
                      decimals: getDecimalByAsset(accountTransfer?.destinationAccount?.asset),
                      suffix: accountTransfer?.destinationAccount?.asset
                    })}
                  </TextCaption>
                </View>
              )}
              {errorMsg?.destinationAccount && (
                <TextCaption danger sx={{ mt: '$1' }}>
                  {errorMsg?.destinationAccount}
                </TextCaption>
              )}
            </View>
            <UniInputAmount
              labelText={t('account-transfer::glossary.amount')}
              assetPrefix={false}
              maxFlag={false}
              sxWrap={{ alignItems: 'center', flexDirection: 'row-reverse' }}
              asset={accountTransfer.asset}
              errorText={errorMsg.amount}
              value={accountTransfer.amount}
              onValueChange={(value) => {
                if (accountTransfer.amount === value) return;
                setAccountTransfer({
                  ...accountTransfer,
                  amount: value
                });
                validationForm({ amount: value });
              }}
              decimals={decimals}
            />
            <View>
              <UniInput
                labelText={t('account-transfer::glossary.description-optional')}
                multiline
                textAlignVertical="top"
                sx={{ height: 97, alignItems: 'flex-start' }}
                inputSx={{ height: 97, lineHeight: 20, pt: 14, pb: 23 }}
                wrapperSx={{ height: 119 }}
                onValueChange={(value) => {
                  setAccountTransfer({
                    ...accountTransfer,
                    description: value
                  });
                }}
              />
            </View>
            <View sx={{ gap: '$1' }}>
              <View sx={{ gap: 10, flexDirection: 'row' }}>
                <TextCaption semiBold sx={{ mb: '$1' }}>
                  {t('card-account-management::glossary.xmetadata')}
                </TextCaption>
                <UniIconButton
                  icon="add"
                  onPress={() => {
                    setXmetadata([...xmetadata, { key: '', value: '' }]);
                  }}
                />
              </View>
              {xmetadata.map((x, i) => (
                <View sx={{ gap: '$3', my: '$1', flexDirection: 'row', alignItems: 'center' }}>
                  <UniInput
                    placeholder={t('card-account-management::glossary.please-input-key')}
                    errorText={errorMsg.xmetadata && errorMsg.xmetadata[i] ? errorMsg.xmetadata[i].key : ''}
                    onValueChange={(value) => {
                      let xmetadataFlag = xmetadata;
                      xmetadataFlag[i].key = value || '';
                      setXmetadata(xmetadataFlag);
                      validationForm({ xmetadata: value });
                    }}
                  />
                  <UniInput
                    placeholder={t('card-account-management::glossary.please-input-value')}
                    onValueChange={(value) => {
                      let xmetadataFlag = xmetadata;
                      xmetadataFlag[i].value = value || '';
                      setXmetadata(xmetadataFlag);
                    }}
                    wrapperSx={{ mb: errorMsg.xmetadata && errorMsg.xmetadata[i] && errorMsg.xmetadata[i].key ? '$5' : undefined }}
                  />
                  <UniIconButton
                    icon="minus"
                    onPress={() => {
                      let xmetadataFlag: Array<{ key: string; value: string }> = [];
                      let xmetadataErrorMsg: Array<{ key: string; value: string }> = errorMsg.xmetadata;
                      xmetadata.forEach((xd, xdi) => {
                        if (xdi != i) xmetadataFlag.push(xd);
                        if (xmetadataErrorMsg && xmetadataErrorMsg[i] && xmetadataErrorMsg[i].key) xmetadataErrorMsg[i].key = '';
                      });
                      setXmetadata(xmetadataFlag);
                      setErrorMsg({ ...errorMsg, xmetadata: xmetadataErrorMsg });
                    }}
                    sx={{ mb: errorMsg.xmetadata && errorMsg.xmetadata[i] && errorMsg.xmetadata[i].key ? '$5' : undefined }}
                  />
                </View>
              ))}
            </View>
          </View>
          {showView && <View sx={{ height: '$2', width: '100%' }} />}
          <View sx={btnView}>
            <UniButton sx={btn} variant={ButtonVariant.outline1} label={t('account-transfer::glossary.button-cancel')} onPress={onClose} />
            <UniButton
              sx={btn}
              labelSx={{ color: '$balck' }}
              label={t('account-transfer::instruction-form.title--account-transfer')}
              onPress={changeAccountsTransfers}
            />
          </View>
        </View>
      </ScrollView>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  body: {
    minHeight: 200,
    minWidth: 500,
    px: 28,
    py: 28
  },
  textMb: {
    marginBottom: 20
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gridTemplateColumns: '1fr 1fr',
    gap: '$3',
    marginTop: 40,
    zIndex: -1
  },
  btn: {
    width: 216,
    height: 44
  },
  accountSx: {
    display: 'flex',
    justifyContent: 'space-between',
    flexDirection: 'row',
    py: '$5',
    px: '$4',
    borderWidth: 1,
    borderColor: '#DCDCDC',
    borderRadius: 5
  }
};
